import type { UserSettingsMap } from "../services/profileConfigService";

export const CASH_BASED_DEPLOYMENT_KEY = "cash_based_deployment";

/**
 * User-settings flag recording that the investor has acknowledged the
 * percentage-based allocation methodology (the first-save education gate).
 * Stored on the user settings config; true once acknowledged.
 */
export const AUTO_DEPLOY_CLARIFICATION_KEY = "auto_deploy_clarification";

/**
 * True only when the user has explicitly acknowledged the allocation
 * methodology. Strict `=== true` so absent / false / any non-boolean value
 * resolves to "not acknowledged" (show the gate). Over-educating is safe;
 * under-educating is the bug this gate fixes.
 */
export function hasAcknowledgedAllocationMethodology(
  userSettings: UserSettingsMap | undefined
): boolean {
  return userSettings?.[AUTO_DEPLOY_CLARIFICATION_KEY] === true;
}

export type CashDeploymentMode = "auto-deploy" | "reserve";

export function resolveCashDeploymentMode(
  userSettings: UserSettingsMap | undefined
): CashDeploymentMode | null {
  const value = userSettings?.[CASH_BASED_DEPLOYMENT_KEY];
  if (value === undefined || value === null) return null;
  return value === true ? "reserve" : "auto-deploy";
}

export function getCashDeploymentSummary(mode: CashDeploymentMode | null): string {
  if (mode === null) return "Default — Auto-deploy";
  return mode === "auto-deploy" ? "Auto-deploy" : "Reserve cash";
}

export function getCashDeploymentSubtext(mode: CashDeploymentMode | null): string {
  if (mode === null) {
    return "You have not made an election yet. New cash auto-deploys to maintain your strategy ratios.";
  }
  if (mode === "auto-deploy") {
    return "New cash is deployed to maintain your current strategy ratios.";
  }
  return "New cash is held undeployed and your allocation percentages adjust to reflect it.";
}

export function getCashDeploymentBannerLabel(mode: CashDeploymentMode | null): string {
  if (mode === null) return "Auto-deploy (default)";
  return mode === "auto-deploy" ? "Auto-deploy" : "Reserve cash";
}

export function cashDeploymentModeToSettingValue(mode: CashDeploymentMode): boolean {
  return mode === "reserve";
}

export type CashDeploymentAdminInput = "unset" | CashDeploymentMode;

export function parseCashDeploymentAdminInput(rawValue: unknown): CashDeploymentAdminInput {
  if (rawValue === undefined || rawValue === null || rawValue === "") return "unset";
  if (typeof rawValue === "boolean") return rawValue ? "reserve" : "auto-deploy";
  const normalized = String(rawValue).trim().toLowerCase();
  if (normalized === "true") return "reserve";
  if (normalized === "false") return "auto-deploy";
  return "unset";
}

export function getCashDeploymentAdminLabel(input: CashDeploymentAdminInput): string {
  if (input === "unset") return getCashDeploymentSummary(null);
  return getCashDeploymentSummary(input);
}

export function cashDeploymentAdminInputToSettingValue(
  input: CashDeploymentAdminInput
): boolean | undefined {
  if (input === "unset") return undefined;
  return cashDeploymentModeToSettingValue(input);
}

export type CashDeploymentDisplayTone = "default" | "auto-deploy" | "reserve";

export function getCashDeploymentDisplay(rawValue: unknown): {
  label: string;
  tone: CashDeploymentDisplayTone;
} {
  const input = parseCashDeploymentAdminInput(rawValue);
  return {
    label: getCashDeploymentAdminLabel(input),
    tone: input === "unset" ? "default" : input,
  };
}
